import React, { useEffect } from "react"
import { connect } from "react-redux"
import { Field, initialize, reduxForm } from "redux-form"
import { FORM_NAMES } from "constants/index"
import { withRouter } from "react-router-dom"
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap"
import PageMetaTags from "components/PageMetaTags"
import BreadcrumbTitle from "components/BreadcrumbTitle"
import { SaveButton } from "components/Buttons"
import {
  createPlace,
  getBusinessUnits,
  getCategories,
  getPlaceData,
  resetPlaceData,
  updatePlace,
} from "store/actions"
import {
  CheckboxField,
  RichTextField,
  SingleSelectField,
  TextField,
} from "components/RenderInput"
import { mapApiResponseToForm, mapPlacesFormToApi } from "./utils"
import validate from "./placesFormValidation"

const PlacesForm = ({
  history,
  businessUnits,
  getBusinessUnits,
  getCategories,
  categories,
  match,
  getPlaceData,
  placeData,
  resetPlaceData,
  initializeForm,
  handleSubmit,
  createPlace,
  vendorId,
  updatePlace,
}) => {
  useEffect(() => {
    if (match.params.id) {
      getPlaceData({ id: match.params.id })
    }
    getBusinessUnits()
    getCategories({ type: 2 })
    return () => {
      resetPlaceData()
    }
  }, [])

  useEffect(() => {
    if (placeData) {
      initializeForm(mapApiResponseToForm(placeData))
    }
  }, [placeData])

  const handleSave = data => {
    if (match?.params?.id) {
      updatePlace({
        values: {
          ...placeData,
          ...mapPlacesFormToApi({ ...data }),
        },
        history: history,
      })
    } else {
      createPlace({
        values: mapPlacesFormToApi({ ...data, vendor_id: vendorId }),
        history: history,
      })
    }
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <PageMetaTags
          title={match?.params?.id ? "Edit Place" : "Create New Place"}
        />
        <Container fluid>
          <BreadcrumbTitle
            title={match?.params?.id ? "Edit Place" : "Create New Place"}
            breadcrumbItems={[
              { title: "Dashboard" },
              {
                title: "Places",
                link: "/places",
              },
              {
                title: match?.params?.id ? "Edit Place" : "Create New Place",
              },
            ]}
          />
          <Row className="d-flex justify-content-end mb-3">
            <Col xs="auto">
              <SaveButton
                type="button"
                color="light"
                content="Back"
                onClick={() => history.goBack()}
              />
            </Col>
          </Row>
          <Card>
            <CardBody>
              <form>
                <Row className="mb-2">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>Business Unit:</label>
                  </Col>
                  <Col xs="auto">
                    <div style={{ width: "250px" }}>
                      <Field
                        name="businessUnit"
                        component={SingleSelectField}
                        options={[
                          {
                            value: null,
                            label: "No Business Unit",
                          },
                          ...businessUnits?.map(bu => ({
                            value: bu?.id,
                            label: bu?.name,
                          })),
                        ]}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>
                      Name<span className="text-danger">*</span>:
                    </label>
                  </Col>
                  <Col>
                    <Field name="name" component={TextField} />
                  </Col>
                </Row>
                <Row className="my-2">
                  <Col xs={12}>
                    <label>Details:</label>
                  </Col>
                  <Col xs={12}>
                    <Field name="details" component={RichTextField} />
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>Category:</label>
                  </Col>
                  <Col xs="auto">
                    <div style={{ width: "250px" }}>
                      <Field
                        name="category"
                        component={SingleSelectField}
                        options={[
                          {
                            value: null,
                            label: "No Category",
                          },
                          ...categories?.map(category => ({
                            value: category?.id,
                            label: category?.name,
                          })),
                        ]}
                      />
                    </div>
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>Hours:</label>
                  </Col>
                  <Col>
                    <Field name="hours" component={TextField} />
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>Search Address:</label>
                  </Col>
                  <Col>
                    <Field name="searchAddress" component={TextField} />
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col xl={6}>
                    <Row>
                      <Col
                        className="mt-2"
                        xl={2}
                        style={{ minWidth: "fit-content" }}
                      >
                        <label>
                          Latitude<span className="text-danger">*</span>:
                        </label>
                      </Col>
                      <Col>
                        <Field name="latitude" component={TextField} />
                      </Col>
                    </Row>
                  </Col>
                  <Col xl={6}>
                    <Row>
                      <Col
                        className="mt-2"
                        xl={2}
                        style={{ minWidth: "fit-content" }}
                      >
                        <label>
                          Longitude<span className="text-danger">*</span>:
                        </label>
                      </Col>
                      <Col>
                        <Field name="longitude" component={TextField} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>
                      Address Street 1<span className="text-danger">*</span>:
                    </label>
                  </Col>
                  <Col>
                    <Field name="addressStreet1" component={TextField} />
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>Address Street 2:</label>
                  </Col>
                  <Col>
                    <Field name="addressStreet2" component={TextField} />
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col xl={6}>
                    <Row>
                      <Col
                        className="mt-2"
                        xl={2}
                        style={{ minWidth: "fit-content" }}
                      >
                        <label>
                          City<span className="text-danger">*</span>:
                        </label>
                      </Col>
                      <Col>
                        <Field name="city" component={TextField} />
                      </Col>
                    </Row>
                  </Col>
                  <Col xl={6}>
                    <Row>
                      <Col
                        className="mt-2"
                        xl={2}
                        style={{ minWidth: "fit-content" }}
                      >
                        <label>
                          State<span className="text-danger">*</span>:
                        </label>
                      </Col>
                      <Col>
                        <Field name="state" component={TextField} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col xl={6}>
                    <Row>
                      <Col
                        className="mt-2"
                        xl={2}
                        style={{ minWidth: "fit-content" }}
                      >
                        <label>Zip Code:</label>
                      </Col>
                      <Col>
                        <Field name="zipCode" component={TextField} />
                      </Col>
                    </Row>
                  </Col>
                  <Col xl={6}>
                    <Row>
                      <Col
                        className="mt-2"
                        xl={2}
                        style={{ minWidth: "fit-content" }}
                      >
                        <label>Country Code:</label>
                      </Col>
                      <Col>
                        <Field name="countryCode" component={TextField} />
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col>
                    <Field
                      name="handicapAccess"
                      label="Handicap Access"
                      component={CheckboxField}
                    />
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col>
                    <Field
                      name="starred"
                      label="Starred"
                      component={CheckboxField}
                    />
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>Internal Notes:</label>
                  </Col>
                  <Col>
                    <Field name="internalNotes" component={TextField} />
                  </Col>
                </Row>
                <Row className="my-3">
                  <Col
                    className="mt-2"
                    xl={1}
                    style={{ minWidth: "fit-content" }}
                  >
                    <label>Coupon:</label>
                  </Col>
                  <Col>
                    <Field name="coupon" component={TextField} />
                  </Col>
                </Row>
                <Row className="d-flex justify-content-center mt-4">
                  <Col xs="auto">
                    <SaveButton
                      type="button"
                      content="Cancel"
                      color="light"
                      onClick={() => history.goBack()}
                    />
                  </Col>
                  <Col xs="auto">
                    <SaveButton
                      type="button"
                      content="Save"
                      onClick={handleSubmit(handleSave)}
                    />
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  businessUnits: state.businessUnits.businessUnits,
  categories: state.Categories.categories,
  placeData: state.PlacesList.placeData,
  vendorId: state.Login.vendorId,
})

const mapDispatchToProps = dispatch => ({
  getBusinessUnits: values => dispatch(getBusinessUnits(values)),
  getCategories: values => dispatch(getCategories(values)),
  getPlaceData: values => dispatch(getPlaceData(values)),
  resetPlaceData: () => dispatch(resetPlaceData()),
  initializeForm: values =>
    dispatch(initialize(FORM_NAMES.PLACES_FEATURE, values)),
  createPlace: values => dispatch(createPlace(values)),
  updatePlace: values => dispatch(updatePlace(values)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({ form: FORM_NAMES.PLACES_FEATURE, validate })(
    withRouter(PlacesForm)
  )
)
